import { useEffect } from 'react';

/**
 * Typeset MathJax inside the given ref element whenever deps change.
 * @param {React.RefObject} ref
 * @param {Array} deps
 * @param {React.RefObject<function>} [onTypesetRef]
 *   Optional ref whose `.current()` is called after each typeset completes.
 *   Pass a ref (not a function) so this hook doesn't need it in its deps.
 */
export function useMathJax(ref, deps = [], onTypesetRef = null) {
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const typeset = () => {
      if (!window.MathJax?.typesetPromise) return;

      // Yield to the browser layout engine so container widths/CQI are calculated first
      setTimeout(() => {
        if (!ref.current) return;

        if (window.MathJax.typesetClear) window.MathJax.typesetClear([el]);
        window.MathJax.typesetPromise([el])
          .then(() => { onTypesetRef?.current?.(); })
          .catch(() => {});
      }, 0);
    };

    if (window.MathJax?.typesetPromise) {
      typeset();
    } else {
      window.addEventListener('mathjax-ready', typeset, { once: true });
      return () => window.removeEventListener('mathjax-ready', typeset);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
}
